import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  Divider,
  Link,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Security as SecurityIcon,
  Phone as PhoneIcon,
  Key as KeyIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import MfaService from '../../services/MfaService';

interface MFAVerificationProps {
  email: string;
  onVerificationSuccess: (token: string) => void;
  onBack?: () => void;
  loading?: boolean;
}

export const MFAVerificationComponent: React.FC<MFAVerificationProps> = ({
  email,
  onVerificationSuccess,
  onBack,
  loading = false
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const [cooldownTime, setCooldownTime] = useState(0);

  // Countdown timer for cooldown
  useEffect(() => {
    if (cooldownTime > 0) {
      const timer = setTimeout(() => {
        setCooldownTime(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownTime]);

  const handleVerification = async () => {
    try {
      setVerifying(true);
      setError(null);

      const code = useBackupCode ? backupCode : verificationCode;
      
      if (!code || (useBackupCode ? code.length < 8 : code.length !== 6)) {
        setError(useBackupCode ? 'Please enter a valid backup code' : 'Please enter a 6-digit verification code');
        return;
      }

      // Call real MFA verification API
      const result = await MfaService.verify({
        email,
        code,
        use_backup_code: useBackupCode
      });

      // Success - authentication token received
      onVerificationSuccess(result.token);

    } catch (err: any) {
      setError(err.message || 'Verification failed');
      setAttemptsRemaining(prev => {
        const remaining = prev - 1;
        if (remaining <= 0) {
          setCooldownTime(300); // 5 minutes cooldown
        }
        return remaining;
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleCodeChange = (value: string) => {
    if (useBackupCode) {
      setBackupCode(value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
    } else {
      setVerificationCode(value.replace(/\D/g, '').slice(0, 6));
    }
    setError(null);
  };

  const isFormValid = () => {
    if (useBackupCode) {
      return backupCode.length >= 8;
    }
    return verificationCode.length === 6;
  };

  const isDisabled = () => {
    return verifying || loading || !isFormValid() || attemptsRemaining <= 0 || cooldownTime > 0;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card sx={{ maxWidth: 400, mx: 'auto' }}>
      <CardContent sx={{ p: 4 }}>
        <Box textAlign="center" mb={3}>
          <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Two-Factor Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please enter the verification code to continue
          </Typography>
        </Box>

        {/* User Info */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary">
            Signing in as:
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {email}
          </Typography>
        </Paper>

        {/* Cooldown Alert */}
        {cooldownTime > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Too many failed attempts. Please wait {formatTime(cooldownTime)} before trying again.
          </Alert>
        )}

        {/* Attempts Warning */}
        {attemptsRemaining <= 2 && attemptsRemaining > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining
          </Alert>
        )}

        {/* Verification Form */}
        <Box mb={3}>
          {!useBackupCode ? (
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PhoneIcon color="primary" />
                <Typography variant="h6">
                  Authenticator Code
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enter the 6-digit code from your authenticator app
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="000000"
                disabled={verifying || loading || cooldownTime > 0}
                inputProps={{ 
                  style: { 
                    fontSize: '1.5rem', 
                    letterSpacing: '0.5rem', 
                    textAlign: 'center' 
                  },
                  maxLength: 6
                }}
                error={!!error && !useBackupCode}
                helperText={error && !useBackupCode ? error : ''}
              />
            </Box>
          ) : (
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <KeyIcon color="primary" />
                <Typography variant="h6">
                  Backup Code
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enter one of your backup codes
              </Typography>
              <TextField
                fullWidth
                label="Backup Code"
                value={backupCode}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="XXXXXXXX"
                disabled={verifying || loading || cooldownTime > 0}
                inputProps={{ 
                  style: { 
                    fontSize: '1.2rem', 
                    letterSpacing: '0.2rem', 
                    textAlign: 'center',
                    fontFamily: 'monospace'
                  }
                }}
                error={!!error && useBackupCode}
                helperText={error && useBackupCode ? error : ''}
              />
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box mb={3}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerification}
            disabled={isDisabled()}
            size="large"
            sx={{ mb: 2 }}
          >
            {verifying ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <span>Verifying...</span>
              </Box>
            ) : (
              'Verify'
            )}
          </Button>

          <Divider sx={{ my: 2 }} />

          <Box textAlign="center">
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setUseBackupCode(!useBackupCode);
                setError(null);
                setVerificationCode('');
                setBackupCode('');
              }}
              disabled={verifying || loading || cooldownTime > 0}
            >
              {useBackupCode ? 'Use authenticator app instead' : 'Use backup code instead'}
            </Link>
          </Box>
        </Box>

        {/* Back Button */}
        {onBack && (
          <Box textAlign="center">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              disabled={verifying || loading}
              variant="text"
            >
              Back to Sign In
            </Button>
          </Box>
        )}

        {/* Help Text */}
        <Box mt={3}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Need help?</strong>
              <br />
              • Codes refresh every 30 seconds
              <br />
              • Each backup code can only be used once
              <br />
              • Contact support if you've lost access to your authenticator
            </Typography>
          </Alert>
        </Box>

        {/* Error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default MFAVerificationComponent;